// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — BroadcastPage (Admin)
// Send notifications to users (global, by role, or specific)
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { Bell, Send, Check, Users, User, Globe } from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { useAuth } from '@contexts/AuthContext';
import { supabase } from '@lib/supabase';
import { PageHeader } from '@components/ui/PageHeader';
import { Card, Button, Input, Textarea, Select, Badge, Grid } from '@components/ui/Primitives';
import { LoadingSpinner } from '@components/ui/States';

export function BroadcastPage() {
  const { theme } = useTheme();
  const { t, lang, isRTL } = useLang();
  const { profile } = useAuth();

  const [recentBroadcasts, setRecentBroadcasts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const [form, setForm] = useState({
    title_ar: '',
    title_en: '',
    body_ar: '',
    body_en: '',
    scope: 'global',
    target_role: 'customer',
    recipient_id: '',
    notification_type: 'info'
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    const [{ data: broadcasts }, { data: userList }] = await Promise.all([
      supabase
        .from('notifications')
        .select('*, sender:sender_id(full_name_ar, full_name_en)')
        .order('created_at', { ascending: false })
        .limit(20),
      supabase.from('profiles').select('id, email, full_name_ar, full_name_en').eq('is_active', true)
    ]);
    setRecentBroadcasts(broadcasts || []);
    setUsers(userList || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSend = async () => {
    if (!form.title_ar || !form.body_ar) return;

    setSending(true);
    const payload = {
      sender_id: profile?.id,
      title_ar: form.title_ar,
      title_en: form.title_en || form.title_ar,
      body_ar: form.body_ar,
      body_en: form.body_en || form.body_ar,
      scope: form.scope,
      notification_type: form.notification_type,
      target_role: form.scope === 'role' ? form.target_role : null,
      recipient_id: form.scope === 'individual' ? form.recipient_id : null
    };

    const { error } = await supabase.from('notifications').insert(payload);

    if (!error) {
      setSent(true);
      setForm({
        title_ar: '', title_en: '', body_ar: '', body_en: '',
        scope: 'global', target_role: 'customer', recipient_id: '',
        notification_type: 'info'
      });
      loadData();
      setTimeout(() => setSent(false), 2500);
    }
    setSending(false);
  };

  const getScopeIcon = (scope) => {
    if (scope === 'global') return <Globe size={12} />;
    if (scope === 'role') return <Users size={12} />;
    return <User size={12} />;
  };

  const getScopeLabel = (scope) => {
    if (scope === 'global') return t.scopeGlobal;
    if (scope === 'role') return t.scopeByRole;
    return t.scopeSpecific;
  };

  if (loading) return <LoadingSpinner />;

  const canSend = form.title_ar && form.body_ar &&
    (form.scope !== 'individual' || form.recipient_id);

  return (
    <div>
      <PageHeader
        icon={<Bell size={22} color={theme.primary} />}
        title={t.broadcast}
        description={lang === 'ar' ? 'إرسال إشعارات للمستخدمين' : 'Send notifications to users'}
      />

      <Grid min={360} style={{ alignItems: 'flex-start' }}>
        {/* Compose form */}
        <Card style={{ padding: 24 }}>
          <h4 style={{
            margin: '0 0 20px',
            fontSize: 15,
            fontWeight: 700,
            color: theme.text,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <Send size={16} color={theme.primary} />
            {t.composeMessage}
          </h4>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{
                fontSize: 11,
                fontWeight: 600,
                color: theme.textSecondary,
                marginBottom: 6,
                display: 'block'
              }}>
                {t.scopeGlobal.split(' ')[0] || 'Scope'}
              </label>
              <Select
                value={form.scope}
                onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}
                options={[
                  { value: 'global', label: t.scopeGlobal },
                  { value: 'role', label: t.scopeByRole },
                  { value: 'individual', label: t.scopeSpecific }
                ]}
              />
            </div>

            {form.scope === 'role' && (
              <Select
                value={form.target_role}
                onChange={e => setForm(f => ({ ...f, target_role: e.target.value }))}
                options={[
                  { value: 'customer', label: t.customer },
                  { value: 'employee', label: t.employee },
                  { value: 'owner', label: t.owner }
                ]}
              />
            )}

            {form.scope === 'individual' && (
              <Select
                value={form.recipient_id}
                onChange={e => setForm(f => ({ ...f, recipient_id: e.target.value }))}
                options={[
                  { value: '', label: t.none },
                  ...users.map(u => ({
                    value: u.id,
                    label: `${lang === 'ar' ? u.full_name_ar : u.full_name_en} · ${u.email}`
                  }))
                ]}
              />
            )}

            <Input
              value={form.title_ar}
              onChange={e => setForm(f => ({ ...f, title_ar: e.target.value }))}
              placeholder={`${t.messageTitle} (${lang === 'ar' ? 'عربي' : 'Arabic'})`}
            />
            <Input
              value={form.title_en}
              onChange={e => setForm(f => ({ ...f, title_en: e.target.value }))}
              placeholder={`${t.messageTitle} (${lang === 'ar' ? 'إنجليزي' : 'English'})`}
            />
            <Textarea
              value={form.body_ar}
              onChange={e => setForm(f => ({ ...f, body_ar: e.target.value }))}
              placeholder={`${t.messageBody} (${lang === 'ar' ? 'عربي' : 'Arabic'})`}
              rows={3}
            />
            <Textarea
              value={form.body_en}
              onChange={e => setForm(f => ({ ...f, body_en: e.target.value }))}
              placeholder={`${t.messageBody} (${lang === 'ar' ? 'إنجليزي' : 'English'})`}
              rows={3}
            />

            <Button onClick={handleSend} disabled={!canSend || sending} fullWidth>
              {sent
                ? <><Check size={14} /> {t.sent}</>
                : sending
                ? t.loading
                : <><Send size={14} /> {t.send}</>
              }
            </Button>
          </div>
        </Card>

        {/* Recent broadcasts */}
        <Card style={{ padding: 24 }}>
          <h4 style={{
            margin: '0 0 16px',
            fontSize: 15,
            fontWeight: 700,
            color: theme.text
          }}>
            {t.recentBroadcasts}
          </h4>
          {recentBroadcasts.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 30,
              color: theme.textTertiary,
              fontSize: 13
            }}>
              {t.noNotifications}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 500, overflowY: 'auto' }}>
              {recentBroadcasts.map(b => (
                <div key={b.id} style={{
                  padding: 12,
                  background: theme.bgInput,
                  borderRadius: 8,
                  border: `1px solid ${theme.borderLight}`
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 6,
                    gap: 8
                  }}>
                    <div style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: theme.text,
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {lang === 'ar' ? b.title_ar : (b.title_en || b.title_ar)}
                    </div>
                    <Badge variant={b.scope === 'global' ? 'primary' : b.scope === 'role' ? 'info' : 'warning'}>
                      {getScopeIcon(b.scope)} {getScopeLabel(b.scope)}
                    </Badge>
                  </div>
                  <p style={{
                    margin: '0 0 6px',
                    fontSize: 12,
                    color: theme.textSecondary,
                    lineHeight: 1.5
                  }}>
                    {lang === 'ar' ? b.body_ar : (b.body_en || b.body_ar)}
                  </p>
                  <div style={{
                    fontSize: 10,
                    color: theme.textTertiary,
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <span>
                      {b.sender
                        ? (lang === 'ar' ? b.sender.full_name_ar : b.sender.full_name_en)
                        : 'System'
                      }
                    </span>
                    <span>{new Date(b.created_at).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-US')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </Grid>
    </div>
  );
}
